import type moment from 'moment'

import {
  add,
  clone,
  date as dateOfMonth,
  format,
  hour,
  isAfter,
  isBetween,
  isSame,
  minute,
  month,
  now,
  parse,
  parseFormat,
  second,
  set,
  year,
} from 'app/lib/date'

type DateValue = moment.MomentInput
type DateMoment = moment.Moment

interface ViewingInfo {
  viewing: {
    guid?: string
    startDate: string
    duration: number
  }
}

export const getDefaultManagerPreferenceStartTime = (
  referenceTime: DateValue = now(),
): DateMoment => {
  const afterOneHour = add(referenceTime, 1, 'hour')
  const minuteAfterOneHour = minute(afterOneHour)
  const roundedMinute = 15 * Math.ceil(minuteAfterOneHour / 15)

  return set(afterOneHour, 'minute', roundedMinute)
}

export const getDefaultViewingRunStartTime = (
  referenceTime: DateValue = now(),
): DateMoment => {
  let startTime = clone(referenceTime)
  startTime = hour(startTime, 9)
  startTime = minute(startTime, 0)
  return second(startTime, 0)
}

export const applyDateToViewingStartTime = (
  startTime: DateValue,
  value: DateValue,
): DateMoment => {
  let nextStartTime = clone(startTime)
  nextStartTime = year(nextStartTime, year(value))
  nextStartTime = month(nextStartTime, month(value))
  return dateOfMonth(nextStartTime, dateOfMonth(value))
}

export const parseViewingRunStartClock = (value: DateValue): DateMoment =>
  parseFormat(value, 'hh:mm A')

export const applyClockTimeToViewingStartTime = (
  startTime: DateValue,
  clockTime: DateValue,
): DateMoment => {
  let nextStartTime = clone(startTime)
  nextStartTime = hour(nextStartTime, hour(clockTime))
  nextStartTime = minute(nextStartTime, minute(clockTime))
  return second(nextStartTime, 0)
}

export const formatViewingRunRequestDate = (
  value: DateValue,
  outputFormat: string,
): string => format(value, outputFormat)

export const formatViewingRunRequestClock = (value: DateValue): string =>
  format(value, 'HH:mm')

export const selectSameDayOrFutureViewingInfos = (
  existingViewingInfos: ViewingInfo[] | undefined | null,
  startTime: DateValue,
): ViewingInfo[] => {
  if (!existingViewingInfos || existingViewingInfos.length === 0) {
    return []
  }

  return existingViewingInfos
    .map((viewingInfo) => viewingInfo)
    .sort((left, right) =>
      left.viewing.startDate.localeCompare(right.viewing.startDate),
    )
    .filter(
      (viewingInfo) =>
        isAfter(viewingInfo.viewing.startDate, startTime) ||
        isSame(viewingInfo.viewing.startDate, startTime, 'day'),
    )
}

export const findConflictedViewing = (
  existingViewingInfos: ViewingInfo[] | undefined | null,
  newViewingStartTime: DateValue | undefined | null,
  newViewingDuration: number,
  newBreakDuration: number,
): ViewingInfo[] => {
  if (
    !existingViewingInfos ||
    existingViewingInfos.length === 0 ||
    !newViewingStartTime
  ) {
    return []
  }

  const newViewingEndTime = add(
    newViewingStartTime,
    newViewingDuration + newBreakDuration,
    'minutes',
  )

  return existingViewingInfos.filter((existingViewingInfo) => {
    const existingViewingStartTime = parse(
      existingViewingInfo.viewing.startDate,
    )
    const existingViewingEndTime = add(
      existingViewingStartTime,
      existingViewingInfo.viewing.duration,
      'minutes',
    )

    return (
      isBetween(
        existingViewingStartTime,
        newViewingStartTime,
        newViewingEndTime,
        undefined,
        '[]',
      ) ||
      isBetween(
        newViewingStartTime,
        existingViewingStartTime,
        existingViewingEndTime,
        undefined,
        '[]',
      )
    )
  })
}
